import { useEffect, useMemo, useState } from 'react';
import { ArchiveRestore } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Link, useLocation } from 'react-router-dom';
import { Disclosure } from '@/components/Disclosure';
import { Hint } from '@/components/Hint';
import { cn } from '@/lib/utils';
import { StatusPill } from '@/components/Status';
import { statusInk, statusPillClass, type StatusTone } from '@/lib/status';
import { buildThreeWay, reopenOrder, type OrderDetail, type OverviewReceiving, type Receipt } from '@/api/orders';
import { getDraftOrder, type DraftDetail } from '@/api/orderPrep';
import { useAuth } from '@/auth/useAuth';
import { isResolvedProblem } from '@/api/packages';
import { dueTone, fmtDate, fmtDateLong, money, pkgStatusLabel } from '../lib';
import { SourceEmailLink } from '../SourceEmailLink';
import { InvoiceSourceChips } from './sourceChips';
import {
  invoicedIssues, orderedIssues, receiptIssues, setupIssues, worstSeverity,
  type Issue, type IssueSeverity,
} from './journeyIssues';

// ---------------------------------------------------------------------------
// The rail: the same four document slots, in the same order, every journey —
// Announced / Ordered / Invoiced / Received — plus shipment ticks and a
// terminal milestone. A node is an eyebrow, a date, one stat line, source
// chips, and its problems folded into one "N issues" line. Nothing on the
// rail is a button: the journey's actions live on the page header, the
// balance report on its tab, the sizing table in To order.
// ---------------------------------------------------------------------------

// A node's dot is the app's status vocabulary (lib/status.ts).
const DOT_FILL: Record<StatusTone, string> = {
  neutral: 'bg-muted-foreground/60',
  success: 'bg-success',
  info: 'bg-info',
  warning: 'bg-warning',
  danger: 'bg-danger',
};
const DOT_OUTLINE: Record<StatusTone, string> = {
  neutral: 'border border-muted-foreground/40 bg-background',
  success: 'border border-success bg-background',
  info: 'border border-info bg-background',
  warning: 'border border-warning bg-background',
  danger: 'border border-danger bg-background',
};
const SEVERITY_DOT: Record<IssueSeverity, string> = {
  danger: 'bg-danger',
  warning: 'bg-warning',
};

function toneFor(issues: Issue[], base: StatusTone): StatusTone {
  const worst = worstSeverity(issues);
  return worst ?? base;
}

function IssueList({ issues }: { issues: Issue[] }) {
  if (issues.length === 0) return null;
  const worst = worstSeverity(issues)!;
  return (
    <Disclosure
      className="mt-1.5"
      label={
        <span className={cn('font-medium', statusInk(worst))}>
          {issues.length} issue{issues.length === 1 ? '' : 's'}
        </span>
      }
    >
      <ul className="space-y-1">
        {issues.map((i) => (
          <li key={i.key} className="flex items-baseline gap-2 text-xs">
            <span className={cn('h-1.5 w-1.5 shrink-0 -translate-y-px rounded-full', SEVERITY_DOT[i.severity])} />
            <Hint content={i.help}><span>{i.text}</span></Hint>
          </li>
        ))}
      </ul>
    </Disclosure>
  );
}

function RailNode({ tone, dashed, title, date, sub, issues, chips, children }: {
  tone: StatusTone;
  dashed?: boolean;
  title: React.ReactNode;
  date?: string | null;
  sub?: React.ReactNode;
  issues?: Issue[];
  chips?: React.ReactNode;
  children?: React.ReactNode;
}) {
  return (
    <div className="relative pl-6">
      <span className={cn('absolute left-0 top-2.25 h-2.75 w-2.75 rounded-full', dashed ? DOT_OUTLINE[tone] : DOT_FILL[tone])} />
      <div className={cn('rounded-lg border p-2.5', dashed ? 'border-dashed border-border/60' : 'border-border/60 bg-background/60')}>
        <div className="flex items-baseline gap-2">
          <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</span>
          {date && <span className="ml-auto shrink-0 text-xs tabular-nums text-muted-foreground">{date}</span>}
        </div>
        {sub && <div className="mt-0.5 text-xs">{sub}</div>}
        {issues && <IssueList issues={issues} />}
        {chips && <div className="mt-1.5 flex flex-wrap items-center gap-1 empty:hidden">{chips}</div>}
        {children}
      </div>
    </div>
  );
}

/**
 * The won't-order milestone: why it closed (the server's sale rule says so in
 * its event; otherwise it was Matt's call) and Reopen, which puts it back on
 * the to-order list.
 */
function WontOrderNode({ detail, onChanged }: { detail: OrderDetail; onChanged?: () => void }) {
  const canReopen = useAuth().can('orders:write:lifecycle') && Boolean(onChanged);
  const [busy, setBusy] = useState(false);
  const auto = [...detail.events].reverse().find((e) => e.eventType === 'wont_order' && e.data?.auto);
  const reopen = async () => {
    setBusy(true);
    try {
      await reopenOrder(detail.id);
      toast.success('Back on the to-order list');
      onChanged?.();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not reopen the journey');
    } finally {
      setBusy(false);
    }
  };
  return (
    <RailNode
      tone="neutral"
      title="Won't order"
      date={fmtDate(detail.archivedAt)}
      sub={<span className="text-muted-foreground">{auto?.summary ?? 'Your call'}</span>}
    >
      {canReopen && (
        <div className="mt-2">
          <Button size="sm" variant="outline" disabled={busy} onClick={() => void reopen()}>
            <ArchiveRestore /> {busy ? 'Reopening…' : 'Reopen'}
          </Button>
        </div>
      )}
    </RailNode>
  );
}

/** A minor event between the document slots — a package moving. */
function RailTick({ children }: { children: React.ReactNode }) {
  return (
    <div className="relative pl-6 text-xs text-muted-foreground">
      <span className="absolute left-0.5 top-1.25 h-1.75 w-1.75 rounded-full bg-border" />
      {children}
    </div>
  );
}

const DRAFT_STATUS: Record<string, string> = {
  building: 'building',
  active: 'sized',
  final: 'final proposal',
  closed: 'closed',
};

/**
 * The prep run's proposal, as one fact line on the pending ORDERED node. The
 * sizing table itself is the journey's Draft tab (and a To-order row's expand).
 */
function RailDraftLine({ orderId, currency }: { orderId: string; currency: string }) {
  const location = useLocation();
  const canPlace = useAuth().can('orders:read:drafts');
  const [draft, setDraft] = useState<DraftDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!canPlace) return;
    let cancelled = false;
    getDraftOrder(orderId)
      .then((d) => { if (!cancelled) { setDraft(d); setError(null); } })
      .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load the prep draft'); });
    return () => { cancelled = true; };
  }, [orderId, canPlace]);

  if (!canPlace) return null;
  if (error) return <div className={cn('mt-1.5 text-xs', statusInk('danger'))}>Draft: {error}</div>;
  if (!draft) return null;

  const release = draft.counts.release;
  const restock = draft.counts.restock;
  const flagged = (release?.flagged ?? 0) + (restock?.flagged ?? 0);
  const unsized = (release?.unsized ?? 0) + (restock?.unsized ?? 0);
  const parts = [
    `Draft · ${DRAFT_STATUS[draft.status] ?? draft.status}`,
    release && release.units > 0
      ? `${release.units} units · ${release.costEstimated ? '~' : ''}${money(release.estCost, currency)}`
      : null,
    restock && restock.lines > 0 ? `+${restock.units} restock` : null,
    unsized > 0 ? `${unsized} unsized` : null,
    flagged > 0 ? `${flagged} flag${flagged === 1 ? '' : 's'}` : null,
    draft.review?.verdict === 'flag' ? 'review flagged' : null,
    draft.refreshedAt ? `sized ${fmtDate(draft.refreshedAt)}` : null,
  ].filter(Boolean);
  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-x-2 text-xs">
      <span className="tabular-nums text-muted-foreground">{parts.join(' · ')}</span>
      <Link
        to="?tab=draft"
        replace
        state={location.state}
        className="text-primary hover:underline"
      >
        Open draft
      </Link>
    </div>
  );
}

export function JourneyRail({ detail, receivings, onChanged }: {
  detail: OrderDetail;
  receivings: OverviewReceiving[];
  /** The journey changed in place (Reopen on a won't-order node). */
  onChanged?: () => void;
}) {
  const stateByReceipt = useMemo(() => new Map(receivings.map((r) => [r.receiptId, r])), [receivings]);
  // The server nulls every cost without orders:read:costs; the rail then
  // states quantities and dates and says nothing about money.
  const showCosts = useAuth().can('orders:read:costs');

  // ------- ANNOUNCED -------
  const announcements = detail.announcements;
  const firstAnn = announcements[0];
  const allReleases = useMemo(() => {
    const seen = new Map<string, { cat_id?: number | null; checked_at?: string | null }>();
    announcements.forEach((a) => a.releaseNames.forEach((rn) => seen.set(rn.name, rn)));
    return [...seen.entries()].map(([name, rn]) => ({ name, ...rn }));
  }, [announcements]);
  const listCapture = announcements.find((a) => a.lineCapture === 'external' || a.lineCapture === 'partial')?.lineCapture;
  const productListRef = detail.refs.find((r) => r.refType === 'product_list_url');
  const announcedIssues = announcements.length > 0 ? setupIssues(detail) : [];

  // ------- ORDERED -------
  const orderSourceIds = useMemo(
    () => Array.from(new Set(detail.orderLines.map((l) => l.sourceMessageId).filter((v): v is string => Boolean(v)))),
    [detail.orderLines]
  );
  const orderedUnits = detail.orderLines.reduce((a, l) => a + (l.qtyOrdered ?? 0), 0);
  const refundedUnits = detail.orderLines.reduce((a, l) => a + (l.qtyRefunded ?? 0), 0);
  const orderedValue = detail.orderLines.reduce(
    (a, l) => (l.qtyOrdered != null && l.unitPrice != null ? a + l.qtyOrdered * l.unitPrice : a), 0);
  const announcedLineCount = detail.counts.announcedLines;
  // The announcement→order coverage check ("ordered 52 of the 70 announced").
  const threeWay = useMemo(() => buildThreeWay(detail), [detail]);
  const announcedKeys = threeWay.filter((r) => r.announced).length;
  const orderedOfAnnounced = threeWay.filter((r) => r.announced && r.qtyOrdered != null).length;
  const orderedIss = orderedIssues(detail);

  // ------- INVOICED -------
  const paidTotals = detail.invoices.map((i) => i.total).filter((t): t is number => t != null);
  const paidSum = paidTotals.reduce((a, b) => a + b, 0);
  const invoicedLineCount = detail.invoices.reduce((a, i) => a + i.lines.length, 0);
  const invoiceBreakdown = (
    <div className="space-y-1">
      {detail.invoices.map((inv) => (
        <div key={inv.id}>
          <span className="font-medium">{inv.invoiceNo || '(no number)'}</span>
          {' — '}
          {[
            inv.productSubtotal != null ? `goods ${money(inv.productSubtotal, inv.currency)}` : null,
            inv.freight ? `freight ${money(inv.freight, inv.currency)}` : null,
            inv.duties ? `duties ${money(inv.duties, inv.currency)}` : null,
            inv.otherFees ? `${inv.otherFeesLabel || 'fees'} ${money(inv.otherFees, inv.currency)}` : null,
            inv.discount ? `discount −${money(inv.discount, inv.currency)}` : null,
            inv.total != null ? `paid ${money(inv.total, inv.currency)}` : 'paid —',
          ].filter(Boolean).join(' · ')}
        </div>
      ))}
    </div>
  );
  const invoicedIss = invoicedIssues(detail);

  // ------- RECEIVED -------
  const receiptTone = (receipt: Receipt, issues: Issue[]): StatusTone => {
    const state = stateByReceipt.get(receipt.id);
    if (receipt.balancedAt) return 'success';
    if (state && state.undecidedDiscrepancies > 0) return 'warning';
    if (state?.ready && state.blockers.length === 0) return 'success';
    return toneFor(issues, 'warning');
  };

  // ------- terminal -------
  const latestBalancedAt = detail.receipts.reduce<string | null>(
    (a, r) => (r.balancedAt && (!a || r.balancedAt > a) ? r.balancedAt : a), null);
  const allReceiptsBalanced = detail.receipts.length > 0 && detail.receipts.every((r) => r.balancedAt);
  const pendingParts = useMemo(() => {
    const open = receivings.filter((r) => detail.receipts.some((rc) => rc.id === r.receiptId && !rc.balancedAt));
    const undecided = open.reduce((a, r) => a + r.undecidedDiscrepancies, 0);
    const items = open.reduce((a, r) => a + r.needs.length + r.blockers.length, 0);
    const parts: string[] = [];
    if (undecided > 0) parts.push(`${undecided} count decision${undecided === 1 ? '' : 's'}`);
    if (items > 0) parts.push(`${items} open item${items === 1 ? '' : 's'}`);
    return parts;
  }, [receivings, detail.receipts]);

  const preOrder = detail.status === 'announced' && detail.invoices.length === 0 && detail.receipts.length === 0;

  return (
    <div className="relative detail-timeline">
      <div className="absolute bottom-3 left-1.25 top-3 w-px bg-border/70" />
      <div className="space-y-5">
        {/* ---- ANNOUNCED ---- */}
        {announcements.length === 0 ? (
          <RailNode tone="neutral" dashed title="Announced" sub={<span className="text-muted-foreground">—</span>} />
        ) : (
          <RailNode
            tone={toneFor(announcedIssues, 'neutral')}
            title={announcements.length > 1 ? `Announced · ${announcements.length}` : 'Announced'}
            date={fmtDate(firstAnn.announcedDate)}
            sub={
              <>
                {announcedLineCount > 0 ? (
                  <span className="font-medium tabular-nums">{announcedLineCount} products</span>
                ) : listCapture === 'external' ? (
                  <span className="text-muted-foreground">product list is external</span>
                ) : '—'}
                {allReleases.length > 0 && (
                  <span className="text-muted-foreground">
                    {' · '}
                    <Hint content={
                      <ul className="space-y-0.5">
                        {allReleases.map((rn) => (
                          <li key={rn.name}>
                            {rn.name} — {rn.cat_id ? 'in the catalog' : rn.checked_at ? 'not set up' : 'not checked yet'}
                          </li>
                        ))}
                      </ul>
                    }>
                      <span>{allReleases.length} release{allReleases.length === 1 ? '' : 's'}</span>
                    </Hint>
                  </span>
                )}
                {listCapture === 'partial' && <span className="text-muted-foreground"> · list partially captured</span>}
              </>
            }
            issues={announcedIssues}
            chips={
              <>
                {announcements.filter((a) => a.sourceMessageId).map((a, i, arr) => (
                  <SourceEmailLink
                    key={a.id}
                    messageId={a.sourceMessageId!}
                    journeyId={detail.id}
                    label={arr.length === 1 ? 'announcement' : `announcement ${i + 1}`}
                  />
                ))}
                {productListRef && (
                  <a href={productListRef.value} target="_blank" rel="noreferrer" className={cn(statusPillClass('neutral'), 'hover:underline')}>
                    product list
                  </a>
                )}
              </>
            }
          />
        )}

        {/* ---- ORDERED ---- */}
        {detail.orderLines.length === 0 ? (
          detail.status === 'announced' ? (
            <RailNode
              tone="neutral"
              dashed
              title="Ordered"
              sub={
                <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <span className="text-muted-foreground">Not ordered yet</span>
                  {detail.orderDueDate && (
                    <StatusPill tone={dueTone(detail.orderDueDate) ?? 'neutral'}>
                      order by {fmtDateLong(detail.orderDueDate)}
                    </StatusPill>
                  )}
                  {detail.expectedAvailability && (
                    <span className="text-xs tabular-nums text-muted-foreground">ships {fmtDate(detail.expectedAvailability)}</span>
                  )}
                </span>
              }
              issues={orderedIss}
            >
              <RailDraftLine orderId={detail.id} currency={detail.currency} />
            </RailNode>
          ) : (
            <RailNode
              tone={toneFor(orderedIss, 'neutral')}
              dashed
              title="Ordered"
              date={fmtDate(detail.placedDate)}
              sub={
                <span className="text-muted-foreground">
                  {detail.orderedCompleteness === 'unavailable' ? 'No itemised order exists for this channel' : 'No confirmation or PO captured'}
                </span>
              }
              issues={orderedIss}
            />
          )
        ) : (
          <RailNode
            tone={toneFor(orderedIss, 'neutral')}
            title="Ordered"
            date={fmtDate(detail.placedDate)}
            sub={
              <>
                <span className="font-medium tabular-nums">
                  {detail.orderLines.length} products{orderedUnits > 0 && ` · ${orderedUnits} units`}
                </span>
                {showCosts && orderedValue > 0 && <span className="tabular-nums text-muted-foreground"> · ≈ {money(orderedValue, detail.currency)} quoted</span>}
                {refundedUnits > 0 && <span className="tabular-nums text-muted-foreground"> · {refundedUnits} refunded</span>}
                {announcedKeys > 0 && (
                  <span className="tabular-nums text-muted-foreground"> · covers {orderedOfAnnounced} of {announcedKeys} announced</span>
                )}
              </>
            }
            issues={orderedIss}
            chips={orderSourceIds.map((id, i) => (
              <SourceEmailLink key={id} messageId={id} journeyId={detail.id} label={orderSourceIds.length === 1 ? 'confirmation' : `confirmation ${i + 1}`} />
            ))}
          />
        )}

        {/* ---- shipment ticks ---- */}
        {detail.packages.map((p) => (
          <RailTick key={p.id}>
            <span className="font-medium text-foreground">{p.carrier || 'Package'}</span>{' '}
            {p.publicUrl ? (
              <a href={p.publicUrl} target="_blank" rel="noreferrer" className="tabular-nums hover:underline">{p.trackingCode}</a>
            ) : (
              <span className="tabular-nums">{p.trackingCode}</span>
            )}
            {' — '}{(p.statusDetail && isResolvedProblem(p) ? p.statusDetail : pkgStatusLabel(p.status)).toLowerCase()}
            {isResolvedProblem(p)
              ? `, resolved${p.problemResolvedAt ? ` ${fmtDate(p.problemResolvedAt)}` : ''}: ${p.problemResolution}`
              : p.status === 'delivered'
                ? p.deliveredAt ? ` ${fmtDate(p.deliveredAt)}` : ''
                : p.estDeliveryDate ? ` · ETA ${fmtDate(p.estDeliveryDate)}` : ''}
          </RailTick>
        ))}

        {/* ---- INVOICED ---- */}
        {preOrder ? (
          <RailTick>then invoice, receiving, balanced</RailTick>
        ) : detail.invoices.length === 0 ? (
          <RailNode
            tone={detail.receipts.length > 0 ? 'warning' : 'neutral'}
            dashed
            title="Invoiced"
            sub={<span className="text-muted-foreground">No invoice captured yet</span>}
            issues={detail.receipts.length > 0
              ? [{ key: 'no-invoice', severity: 'warning', text: 'Goods received with no invoice', help: 'Nothing to balance against until an invoice is captured.' }]
              : undefined}
          />
        ) : (
          <RailNode
            tone={toneFor(invoicedIss, 'neutral')}
            title={detail.invoices.length > 1 ? `Invoiced · ${detail.invoices.length}` : 'Invoiced'}
            date={detail.invoices.length === 1 ? fmtDate(detail.invoices[0].invoiceDate) : undefined}
            sub={
              <>
                {showCosts && (
                  <Hint content={invoiceBreakdown}>
                    <span className="font-medium tabular-nums">{paidTotals.length > 0 ? money(paidSum, detail.currency) : '—'}</span>
                  </Hint>
                )}
                <span className={cn('tabular-nums', showCosts ? 'text-muted-foreground' : 'font-medium')}>
                  {detail.invoices.length === 1
                    ? <>{showCosts && ' · '}{detail.invoices[0].invoiceNo || '(no number)'} · {detail.invoices[0].lines.length} products</>
                    : <>{showCosts && ' · '}{invoicedLineCount} products</>}
                </span>
              </>
            }
            issues={invoicedIss}
            chips={detail.invoices.map((inv) => <InvoiceSourceChips key={inv.id} inv={inv} journeyId={detail.id} />)}
          />
        )}

        {/* ---- RECEIVED — one slot per receiving ---- */}
        {detail.receipts.length === 0 ? (
          preOrder ? null : (
            <RailNode tone="neutral" dashed title="Received" sub={<span className="text-muted-foreground">No receiving recorded yet</span>} />
          )
        ) : (
          detail.receipts.map((receipt) => {
            const state = stateByReceipt.get(receipt.id);
            const issues = receiptIssues(detail, receipt, state);
            const units = receipt.lines.reduce((a, l) => a + (l.qtyReceived ?? 0), 0);
            return (
              <RailNode
                key={receipt.id}
                tone={receiptTone(receipt, issues)}
                title={`Received — #${receipt.receivingId}`}
                date={fmtDate(receipt.dateCreated)}
                sub={
                  <>
                    <span className="font-medium tabular-nums">{receipt.lines.length} products{units > 0 && ` · ${units} units`}</span>
                    {showCosts && receipt.bookedTotal != null && <span className="tabular-nums text-muted-foreground"> · booked {money(receipt.bookedTotal, detail.currency)}</span>}
                    {receipt.balancedAt && <span className={statusInk('success')}> · balanced {fmtDate(receipt.balancedAt)}</span>}
                    {!receipt.balancedAt && state?.ready && state.blockers.length === 0 && <span className={statusInk('success')}> · ready to balance</span>}
                  </>
                }
                issues={issues}
              />
            );
          })
        )}

        {/* ---- terminal milestone ---- */}
        {detail.status === 'cancelled' ? (
          <RailNode tone="neutral" title="Cancelled" date={fmtDate(detail.archivedAt)} />
        ) : detail.status === 'wont_order' ? (
          <WontOrderNode detail={detail} onChanged={onChanged} />
        ) : detail.receipts.length > 0 ? (
          allReceiptsBalanced ? (
            <RailNode tone="success" title="Balanced" date={fmtDate(latestBalancedAt)} sub={<span className={statusInk('success')}>Every receiving costed and written back</span>} />
          ) : (
            <RailNode
              tone="neutral"
              dashed
              title="Balanced"
              sub={<span className="text-muted-foreground">{pendingParts.length > 0 ? `pending · ${pendingParts.join(' · ')}` : 'pending · ready to apply cost edits'}</span>}
            />
          )
        ) : null}
      </div>
    </div>
  );
}
